import React from 'react';
import { Grid, Card, Typography, Row, Col } from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend
} from 'recharts';
import { ArrowUpOutlined, ArrowDownOutlined, MinusOutlined } from '@ant-design/icons';
import { formatDate } from '../../utils/dateFormatters';
import { filterByNits } from '../../utils/inventoryReports';

const { useBreakpoint } = Grid;
const { Text, Paragraph } = Typography;

/**
 * CriticalTrendLine - Line chart showing critical inventory trends over time
 *
 * MÉTODO DE CÁLCULO DE LA TENDENCIA CRÍTICA:
 *
 * 1. AGREGACIÓN DE DATOS:
 *    - Se agrupan los SKUs por fecha (RecordDate)
 *    - Se suman los conteos de Negro y Rojo independientemente
 *    - Cada punto representa el conteo total de SKUs en estado crítico por día
 *
 * 2. CATEGORÍAS CRÍTICAS:
 *    - Negro (<= 10% inventario): SKUs con inventario críticamente bajo
 *    - Rojo (10-30% inventario): SKUs con inventario peligrosamente bajo
 *    - Ambos requieren atención inmediata para evitar stockouts
 *
 * 3. ANÁLISIS DE TENDENCIA:
 *    - Se compara el valor más reciente con el valor más antiguo
 *    - Tendencia Creciente: Aumento de SKUs críticos (situación adversa)
 *    - Tendencia Decreciente: Disminución de SKUs críticos (mejora)
 *    - Tendencia Estable: Sin cambios significativos
 *
 * 4. PORCENTAJE DE CRITICIDAD:
 *    - % Crítico = (Negro + Rojo) / Total SKUs × 100
 *    - Este porcentaje indica qué parte del inventario está en riesgo
 *
 * @param {Object} props
 * @param {Array} props.data - Array of DailyInventoryDistributionRecord
 * @param {string[]} props.nitList - Selected NITs for filtering
 */
function CriticalTrendLine({ data, nitList }) {
  const screens = useBreakpoint();
  const isXs = !screens.xs;
  const isSm = screens.sm;

  // Transform data for chart - aggregate by date if grouped by NIT
  const chartData = React.useMemo(() => {
    if (!data || data.length === 0) return [];

    const relevantData = filterByNits(data, nitList);

    // If data has multiple records per date (grouped by NIT), aggregate
    const groupedByDate = {};
    relevantData.forEach(record => {
      const date = record.RecordDate;
      if (!groupedByDate[date]) {
        groupedByDate[date] = {
          RecordDate: date,
          NegroCount: 0,
          RojoCount: 0,
          TotalReferences: 0
        };
      }
      groupedByDate[date].NegroCount += record.NegroCount || 0;
      groupedByDate[date].RojoCount += record.RojoCount || 0;
      groupedByDate[date].TotalReferences += record.TotalReferences || 0;
    });

    return Object.values(groupedByDate)
      .sort((a, b) => a.RecordDate.localeCompare(b.RecordDate));
  }, [data, nitList]);

  // Calculate trend analysis
  const trendAnalysis = React.useMemo(() => {
    if (!chartData || chartData.length < 2) return null;

    const firstData = chartData[0];
    const lastData = chartData[chartData.length - 1];

    const firstCritical = firstData.NegroCount + firstData.RojoCount;
    const lastCritical = lastData.NegroCount + lastData.RojoCount;
    const change = lastCritical - firstCritical;
    const changePercent = firstCritical > 0 ? ((change / firstCritical) * 100) : 0;

    // Get latest criticality percentage
    const latestTotal = lastData.TotalReferences || 1;
    const criticalityPercent = ((lastData.NegroCount + lastData.RojoCount) / latestTotal) * 100;

    if (change > 0) {
      return {
        direction: 'increasing',
        label: 'Tendencia Creciente',
        color: '#dc2626',
        bgColor: '#fee2e2',
        borderColor: '#fca5a5',
        description: 'El número de SKUs críticos está aumentando. Se requiere acción inmediata.',
        icon: <ArrowUpOutlined />,
        change,
        changePercent,
        criticalityPercent
      };
    } else if (change < 0) {
      return {
        direction: 'decreasing',
        label: 'Tendencia Decreciente',
        color: '#16a34a',
        bgColor: '#dcfce7',
        borderColor: '#86efac',
        description: 'El número de SKUs críticos está disminuyendo. Evolución positiva.',
        icon: <ArrowDownOutlined />,
        change,
        changePercent,
        criticalityPercent
      };
    } else {
      return {
        direction: 'stable',
        label: 'Tendencia Estable',
        color: '#6b7280',
        bgColor: '#f3f4f6',
        borderColor: '#d1d5db',
        description: 'El número de SKUs críticos se mantiene constante. Monitorear continuamente.',
        icon: <MinusOutlined />,
        change: 0,
        changePercent: 0,
        criticalityPercent
      };
    }
  }, [chartData]);

  // Responsive settings
  const chartHeight = isXs ? 220 : isSm ? 300 : 380;
  const marginValue = isXs ? 8 : 20;

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    const negroEntry = payload.find(p => p.dataKey === 'NegroCount');
    const rojoEntry = payload.find(p => p.dataKey === 'RojoCount');
    const totalCritical = (negroEntry?.value || 0) + (rojoEntry?.value || 0);
    const totalReferences = chartData.find(d => d.RecordDate === label)?.TotalReferences || 1;
    const criticalPercent = (totalCritical / totalReferences) * 100;

    return (
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        minWidth: '160px'
      }}>
        <p style={{ margin: '0 0 8px 0', fontWeight: 600, color: '#1f2937' }}>
          {formatDate(label)}
        </p>
        <p style={{ margin: '4px 0', color: '#1f2937', fontWeight: 500 }}>
          <span style={{ color: '#1f2937' }}>Negro:</span> {negroEntry?.value || 0} SKUs
        </p>
        <p style={{ margin: '4px 0', color: '#dc2626', fontWeight: 500 }}>
          <span style={{ color: '#dc2626' }}>Rojo:</span> {rojoEntry?.value || 0} SKUs
        </p>
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #e5e7eb' }}>
          <p style={{ margin: 0, fontWeight: 600, color: '#6b7280', fontSize: '11px' }}>
            Total Crítico: {totalCritical} SKUs ({criticalPercent.toFixed(1)}%)
          </p>
        </div>
      </div>
    );
  };

  if (!chartData || chartData.length === 0) {
    return (
      <Card
        bordered={false}
        className="shadow-sm"
        style={{
          height: '100%',
          borderRadius: '12px',
          backgroundColor: '#fafafa'
        }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 300,
          color: '#9ca3af',
          textAlign: 'center',
          padding: 20
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>{'📈'}</div>
          <div style={{ fontSize: isXs ? 14 : 16, fontWeight: 500, marginBottom: 8 }}>
            Datos insuficientes
          </div>
          <div style={{ fontSize: isXs ? 12 : 13 }}>
            Se requieren datos de inventario para mostrar la tendencia crítica
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      bordered={false}
      className="shadow-sm"
      style={{
        height: '100%',
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
      }}
      bodyStyle={{ padding: isXs ? 16 : 20 }}
    >
      {/* Header */}
      <div style={{ marginBottom: isXs ? 16 : 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Text strong style={{ fontSize: isXs ? 14 : 16, color: '#1f2937' }}>
            {'Tendencia Crítica'}
          </Text>
        </div>
        <Text type="secondary" style={{ fontSize: isXs ? 11 : 12 }}>
          Productos en riesgo de agotarse (sin stock o con muy poco stock). Si la línea sube, hay más productos en peligro.
        </Text>
      </div>

      {/* Trend Indicator */}
      {trendAnalysis && (
        <div style={{
          marginBottom: isXs ? 16 : 20,
          padding: '12px 16px',
          backgroundColor: trendAnalysis.bgColor,
          borderRadius: '10px',
          borderLeft: `4px solid ${trendAnalysis.color}`,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <span style={{ fontSize: 20, color: trendAnalysis.color }}>
            {trendAnalysis.icon}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Text strong style={{ color: trendAnalysis.color, fontSize: isXs ? 13 : 14 }}>
                {trendAnalysis.label}
              </Text>
              <Text style={{ fontSize: isXs ? 11 : 12, color: '#6b7280' }}>
                ({trendAnalysis.changePercent > 0 ? '+' : ''}{trendAnalysis.changePercent.toFixed(1)}%)
              </Text>
            </div>
            <Text type="secondary" style={{ fontSize: isXs ? 11 : 12, display: 'block' }}>
              {trendAnalysis.description}
            </Text>
          </div>
          <div style={{ textAlign: 'right' }}>
            <Text style={{ fontSize: isXs ? 10 : 11, color: '#6b7280', display: 'block' }}>
              Criticidad Actual
            </Text>
            <Text strong style={{ fontSize: isXs ? 16 : 18, color: trendAnalysis.color }}>
              {trendAnalysis.criticalityPercent.toFixed(1)}%
            </Text>
          </div>
        </div>
      )}

      {/* Chart */}
      <div style={{ marginBottom: isXs ? 16 : 20 }}>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <LineChart
            data={chartData}
            margin={{ top: marginValue, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="RecordDate"
              tickFormatter={formatDate}
              angle={isXs ? -90 : -45}
              textAnchor="end"
              height={isXs ? 50 : 80}
              style={{ fontSize: isXs ? 9 : 11, color: '#6b7280' }}
              interval={isXs ? 1 : 0}
            />
            <YAxis
              style={{ fontSize: isXs ? 9 : 11, color: '#6b7280' }}
              label={{ value: 'SKUs', angle: -90, position: 'insideLeft', style: { fontSize: '11px' } }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: isXs ? 10 : 12, paddingTop: 8 }}
              iconType="circle"
            />
            <Line
              type="monotone"
              dataKey="NegroCount"
              name={isXs ? 'Negro (<10%)' : 'Negro (<10% inventario)'}
              stroke="#1f2937"
              strokeWidth={2}
              dot={{ r: isXs ? 3 : 4, fill: '#1f2937', strokeWidth: 2 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="RojoCount"
              name={isXs ? 'Rojo (10-30%)' : 'Rojo (10-30% inventario)'}
              stroke="#dc2626"
              strokeWidth={2}
              dot={{ r: isXs ? 3 : 4, fill: '#dc2626', strokeWidth: 2 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Statistics Summary */}
      {chartData.length > 0 && (
        <div style={{
          marginBottom: isXs ? 16 : 20,
          padding: '12px 16px',
          backgroundColor: '#f9fafb',
          borderRadius: '10px',
          border: '1px solid #e5e7eb'
        }}>
          <Row gutter={[12, 12]}>
            <Col xs={12} sm={8}>
              <div style={{ textAlign: 'center' }}>
                <Text style={{ fontSize: isXs ? 10 : 11, color: '#6b7280' }}>Mínimo</Text>
                <div style={{ fontSize: isXs ? 16 : 18, fontWeight: 700, color: '#1f2937' }}>
                  {Math.min(...chartData.map(d => d.NegroCount + d.RojoCount))}
                </div>
              </div>
            </Col>
            <Col xs={12} sm={8}>
              <div style={{ textAlign: 'center' }}>
                <Text style={{ fontSize: isXs ? 10 : 11, color: '#6b7280' }}>Máximo</Text>
                <div style={{ fontSize: isXs ? 16 : 18, fontWeight: 700, color: '#dc2626' }}>
                  {Math.max(...chartData.map(d => d.NegroCount + d.RojoCount))}
                </div>
              </div>
            </Col>
            <Col xs={12} sm={8}>
              <div style={{ textAlign: 'center' }}>
                <Text style={{ fontSize: isXs ? 10 : 11, color: '#6b7280' }}>Promedio</Text>
                <div style={{ fontSize: isXs ? 16 : 18, fontWeight: 700, color: '#6b7280' }}>
                  {Math.round(chartData.reduce((sum, d) => sum + d.NegroCount + d.RojoCount, 0) / chartData.length)}
                </div>
              </div>
            </Col>
          </Row>
        </div>
      )}

      {/* Methodology */}
      <div style={{
        backgroundColor: '#f9fafb',
        borderRadius: '10px',
        padding: isXs ? 14 : 16,
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong style={{ fontSize: isXs ? 12 : 13, color: '#1f2937' }}>
            {'Como se calcula'}
          </Text>
        </div>

        <Paragraph style={{
          margin: '0 0 12px 0',
          fontSize: isXs ? 11 : 12,
          color: '#6b7280',
        }}>
          Se cuentan los productos con inventario critico (Negro: sin stock, Rojo: stock muy bajo) por cada dia. La tendencia compara el primer dia con el ultimo.
        </Paragraph>

        {/* ¿Qué hacer? */}
        {trendAnalysis && (
          <div style={{
            padding: '10px 12px',
            backgroundColor: trendAnalysis.direction === 'increasing' ? '#fff7ed' : trendAnalysis.direction === 'decreasing' ? '#f0fdf4' : '#f9fafb',
            borderRadius: '8px',
            border: `1px solid ${trendAnalysis.direction === 'increasing' ? '#fed7aa' : trendAnalysis.direction === 'decreasing' ? '#bbf7d0' : '#e5e7eb'}`
          }}>
            <Text strong style={{ fontSize: isXs ? 11 : 12, color: '#1f2937' }}>
              {'Que hacer: '}
            </Text>
            <Text style={{ fontSize: isXs ? 11 : 12, color: '#6b7280' }}>
              {trendAnalysis.direction === 'increasing'
                ? 'Revisa los pedidos pendientes con tus proveedores. Prioriza los productos en Negro y Rojo.'
                : trendAnalysis.direction === 'decreasing'
                  ? 'Buen trabajo. Los productos criticos estan disminuyendo. Continua con el plan actual.'
                  : 'Los productos criticos se mantienen estables. Monitorea diariamente para detectar cambios.'
              }
            </Text>
          </div>
        )}
      </div>

      {/* Pro Tip */}
      <div style={{
        marginTop: 12,
        padding: '10px 12px',
        backgroundColor: '#eff6ff',
        borderRadius: '8px',
        border: '1px solid #bfdbfe',
        fontSize: isXs ? 10 : 11,
        color: '#1e40af',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8
      }}>
        <span style={{ fontSize: 14, flexShrink: 0 }}>💡</span>
        <Text style={{ fontSize: isXs ? 10 : 11 }}>
          <strong>Tip:</strong> Monitorea este gráfico diariamente. Un aumento sostenido en la tendencia crítica indica problemas en el abastecimiento o cambios en la demanda.
        </Text>
      </div>
    </Card>
  );
}

export default CriticalTrendLine;
